// src/pages/network/NetworkHome.js (modified)
import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useNetwork } from '../../context/NetworkContext';
import { useAuth } from '../../context/AuthContext';
import { 
  Globe, LogIn, TrendingUp, Users, Briefcase, ArrowRight 
} from 'lucide-react';
import PostCreator from '../../components/network/feed/PostCreator';
import PostList from '../../components/network/feed/PostList';
import TrendingTopics from '../../components/network/discover/TrendingTopics';
import BusinessSuggestions from '../../components/network/discover/BusinessSuggestions';

const NetworkHome = () => {
  const [feedPosts, setFeedPosts] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  
  const { currentUser } = useAuth();
  const { loadFeed } = useNetwork();
  const { placement } = useOutletContext() || {};
  
  // Load initial posts
  useEffect(() => {
    const initializeFeed = async () => {
      setLoading(true);
      const { posts, lastVisible: newLastVisible } = await loadFeed();
      
      setFeedPosts(posts);
      setLastVisible(newLastVisible);
      setHasMore(posts.length === 10);
      setLoading(false);
    };
    
    initializeFeed();
  }, [loadFeed]);
  
  // Only render content for the appropriate placement
  if (placement === 'right-sidebar') {
    return null; // Right sidebar content is handled by NetworkRightSidebar
  }
  
  // Left sidebar content
  if (placement === 'left-sidebar') {
    return (
      <div className="space-y-6">
        {!currentUser ? (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Why Join Our Network?</h3>
            <ul className="space-y-3">
              <li className="flex">
                <Users className="h-5 w-5 text-[#190192] flex-shrink-0 mr-2" />
                <span className="text-gray-600">Connect with businesses in your industry</span>
              </li>
              <li className="flex">
                <TrendingUp className="h-5 w-5 text-[#190192] flex-shrink-0 mr-2" />
                <span className="text-gray-600">Share insights and best practices</span>
              </li>
              <li className="flex">
                <Briefcase className="h-5 w-5 text-[#190192] flex-shrink-0 mr-2" />
                <span className="text-gray-600">Discover collaboration opportunities</span>
              </li>
            </ul>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <Link 
                to="/register" 
                className="text-[#190192] hover:text-[#2C0DB5] font-medium inline-flex items-center"
              >
                Join now <ArrowRight size={16} className="ml-1" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <BusinessSuggestions />
          </div>
        )}
        
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <TrendingTopics />
        </div>
      </div>
    );
  }
  
  // Load more posts
  const handleLoadMore = async () => {
    if (!hasMore || loading || !lastVisible) return;
    
    setLoading(true);
    const { posts, lastVisible: newLastVisible } = await loadFeed(lastVisible);
    
    if (posts.length > 0) {
      setFeedPosts(prev => [...prev, ...posts]);
      setLastVisible(newLastVisible);
      setHasMore(posts.length === 10);
    } else {
      setHasMore(false);
    }
    
    setLoading(false);
  };
  
  // Add a new post to feed
  const handleNewPost = (newPost) => {
    setFeedPosts(prev => [newPost, ...prev]);
  };

  const handleUpdatePost = (updatedPost) => {
    setFeedPosts((prev) => prev.map((post) => (post.id === updatedPost.id ? { ...post, ...updatedPost } : post)));
  };

  const handleDeletePost = (postId) => {
    setFeedPosts((prev) => prev.filter((post) => post.id !== postId));
  };
  
  // Main content (default placement)
  return (
    <>
      {!currentUser && (
        <div className="bg-gradient-to-r from-[#190192] to-[#3B0DB5] rounded-xl shadow-lg mb-8 overflow-hidden">
          <div className="px-6 py-8 md:py-12 md:px-12 text-white md:flex items-center justify-between">
            <div className="mb-6 md:mb-0 md:mr-8">
              <h2 className="text-2xl font-bold mb-2">Welcome to BizCentral Business Network</h2>
              <p className="text-purple-200 text-lg mb-4">
                Connect with other businesses, share insights, and grow your network
              </p>
              <div className="flex flex-wrap gap-4 mt-6">
                <Link
                  to="/login"
                  className="px-6 py-2.5 bg-white text-[#190192] rounded-lg hover:bg-gray-100 font-medium"
                >
                  Sign In
                </Link>
                // src/pages/network/NetworkHome.js (continued)
                <Link
                  to="/register"
                  className="px-6 py-2.5 border border-white text-white rounded-lg hover:bg-white/10 font-medium"
                >
                  Create Account
                </Link>
              </div>
            </div>
            <div className="hidden md:block">
              <Globe size={120} className="text-white/30" />
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {currentUser && (
          <PostCreator onPostCreated={handleNewPost} />
        )}
        
        <PostList 
          posts={feedPosts} 
          loading={loading}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          isAuthenticated={!!currentUser}
          onUpdatePost={handleUpdatePost}
          onDeletePost={handleDeletePost}
        />
      </div>
    </>
  );
};

export default NetworkHome;
